// Library imports
import { computed, makeObservable, observable } from 'mobx';

// Local imports
import { CrossSectionLogpoint } from './CrossSectionLogpoint';
import { CrossSectionAlignment } from './CrossSectionAlignment';
import { CrossSectionPipe } from './CrossSectionPipe';
import { CrossSectionModel } from './CrossSectionModel';
import { CrossSectionPointsMeasurement } from './CrossSectionPointsMeasurement';
import { Ajax2 } from '@shared/Ajax';
import { ProjectCodesService } from '@shared/services/ProjectCodesService';
import { Notify } from '@shared/ui-native/Notify';
import { FileTreeService } from '@shared/services/FileTreeService';
import { CrossSectionUtils } from '@pages/cross-section/shared/CrossSectionUtils';
import { CrossSectionCanvas } from '@pages/cross-section/shared/CrossSectionCanvas';
import { latLonToEN } from '@openlayers/ol-utils';
import { CoordsUtils, NorthingEasting } from '@shared/utils/CoordsUtils';
import { Vector2 } from '@math.gl/core';
import { CrossSectionPoint } from './CrossSectionPoint';
import { Events } from '@shared/Events';
import { ICrossSectionModel, ICrossSectionVertex, CrossSectionViewState, ICrossSectionPoint } from '@pages/cross-section/shared/CrossSectionWindow';
import { getPipeElevationOffset } from '@shared/utils/PipeUtils';

// Reality capture
import { crossSectionCoordsMeta } from '@shared/reality-capture/utils/common';
import { lumiDbStore } from '@shared/reality-capture/lumidb-store';
import { demStore } from '@shared/reality-capture/dem-store';

interface Point {
    x: number;
    y: number;
    id?: number;
    color?: string;
}

interface Alignment {
    id: number;
    name?: string;
    modelId: number;
    point: Point;
    color: string;
    props: {
        name: string;
    };
    intersectionStation: number;
}

interface Border {
    point: Point;
    color: string;
}

interface Pipe {
    point: Point;
    modelId: number;
    name: string;
    props: {
        elevType?: string;
        pipeNetType?: string;
        diameter?: string;
        thickness?: string;
    };
}

interface Logpoint {
    id: number;
    x: number;
    y: number;
    z: number;
    modelId: number;
    pointName?: string;
    number?: string;
    code?: string;
    type?: string;
    tol: number;
    modelDeltaZ?: number;
    tolerance: {
        upperLimit?: number;
        lowerLimit?: number;
    };
}

export interface PointCloudPoint {
    id: string;
    name: string;
    point: Vector2;
    color: [number, number, number];
    visible: boolean;
    csCanvas: CrossSectionCanvas;
    sourceFileId?: number;
    sourceFileUuid?: string;
    onClickCallback: () => void;
}

interface ICrossSectionData {
    projectId: number;
    alignmentId: number;
    alignmentIndex: number;
    w: number;
    logpointDelta: number;
    folderId?: number;
    includeModelIds?: string;
    strictModelSelection?: boolean;
    includeLogpointIds?: number[];
    // For free cross-section
    lat1?: number;
    lon1?: number;
    lat2?: number;
    lon2?: number;
    // For regular cross-section
    station?: number;
}

export interface ICrossSectionResponse {
    freeCrossSection: boolean;
    width: number;
    height: number;
    logpointDelta: number;
    borders: Border[];
    customBorders: Border[];
    pipes: Pipe[];
    logpoints: Logpoint[];
    models: ICrossSectionModel[];
    terrainModels: ICrossSectionModel[];
    alignment: Alignment;
    backgroundAlignments: Alignment[];
    stationEquation: string;
    station: number;
    gridCoordiantes: unknown | null;
    coordinates: {
        width: number;
        coords1: {
            n: number;
            e: number;
            z: number;
        };
        coords2: {
            n: number;
            e: number;
            z: number;
        };
        center: {
            n: number;
            e: number;
            z: number;
        };
    };
}

// data set on init to define what is loaded on scene
export interface ICrossSectionLoadSelection {
    alignmentId: number;
    alignmentIndex: number;
    folderId?: number;
    includeModelIds?: number[];
    // When true, the model selection is exhaustive: load ONLY the models in
    // `includeModelIds` (alongside the explicitly selected DEMs/point clouds),
    // and never fall back to loading every model in the project — even when
    // `includeModelIds` is empty (e.g. a DEM-only selection). When omitted the
    // server keeps its default behaviour of loading all models if none are given.
    strictModelSelection?: boolean;
    includeLumiDbUuids?: string[]; // Use UUIDs with point clouds since they are stored to LumiDB
    includeDemUuids?: string[]; // Use UUIDs with DEM since they are stored to AWS S3
    includeLogpointIds?: number[];
    selectedLogpointId?: number;
    selectedPipeId?: number;
    leftBorder?: number;
    rightBorder?: number;
}

export class CrossSectionStore {
    csCanvas: CrossSectionCanvas = null;
    loadSelection: ICrossSectionLoadSelection = null;
    latestServerData: ICrossSectionResponse = null;

    initialPositionDone = false;
    isLoading = false;
    isLoadingAsync = false;
    isLoadingTree = true;
    isCreatingPdf = false;

    modelNamesMap: Record<number, string> = {};
    hiddenPipeIds: number[] = [];
    hiddenLogpointsByModelId: number[] = [];
    hiddenModelIds: Array<string | number> = [];
    showMobileModelsList = false;
    showMobileMoreToolbar = false;

    // Reality capture
    public realityCaptureIncluded: boolean;
    public asPointCloud: boolean = false;
    selectedRcUuids: string[] = [];
    hiddenRcUuids: string[] = [];
    realityCapturePoints: PointCloudPoint[] = [];
    realityCaptureModels: ICrossSectionModel[] = [];
    realityCaptureNamesMap: Record<number, string> = {};

    // distance measurements
    clickedVertex0: ICrossSectionVertex = null;
    clickedVertex1: ICrossSectionVertex = null;
    clickedPoints: ICrossSectionVertex[] = [];

    currentInfoItem: string = null;

    get getModelsControls() {
        if (!this.latestServerData) {
            return [];
        }
        return CrossSectionUtils.getModelsControls(this.latestServerData.models, this.getVisibleLogpoints(), this.latestServerData.pipes, this.modelNamesMap);
    }

    readonly freeCrossSectionCoords = {
        lat1: null,
        lat2: null,
        lon1: null,
        lon2: null,
    };

    get isFreeCrossSection() {
        return (
            this.freeCrossSectionCoords.lat1 !== null &&
            this.freeCrossSectionCoords.lat2 !== null &&
            this.freeCrossSectionCoords.lon1 !== null &&
            this.freeCrossSectionCoords.lon2 !== null
        );
    }

    settings = {
        station: 0,
        step: 10,
        searchDisplacement: kuura.getCrossSectionSettings().logpointSearchDisplacement ?? 2.5,
        deltaZFilter: 0,
    };

    constructor(private treeData?: ITreeData, private view?: CrossSectionViewState, private onFetch?: (selection: ICrossSectionResponse) => void) {
        this.localStorageLoad();
        // Set both point clouds and DEMs true for all users (for now)
        this.realityCaptureIncluded = true;

        makeObservable(this, {
            csCanvas: observable.ref,
            loadSelection: observable,
            latestServerData: observable.ref,
            isLoading: observable,
            isLoadingAsync: observable,
            isLoadingTree: observable,
            isCreatingPdf: observable,
            hiddenPipeIds: observable,
            hiddenLogpointsByModelId: observable,
            hiddenModelIds: observable,
            hiddenRcUuids: observable,
            showMobileModelsList: observable,
            showMobileMoreToolbar: observable,
            selectedRcUuids: observable.ref,
            modelNamesMap: observable,
            realityCaptureNamesMap: observable,
            currentInfoItem: observable,
            clickedVertex0: observable.ref,
            clickedVertex1: observable.ref,
            clickedPoints: observable,
            realityCapturePoints: observable,
            realityCaptureModels: observable,
            realityCaptureIncluded: observable,
            getModelsControls: computed,
            freeCrossSectionCoords: observable.ref,
            isFreeCrossSection: computed,
            settings: observable,
            asPointCloud: observable,
        });

        this.fetchInitialData();
    }

    toggleRcMode = () => {
        this.asPointCloud = !this.asPointCloud;
        this.drawAll();
    };

    destroy() {
        this.sessionStorageSaveView();
        if (this.csCanvas) {
            this.csCanvas.destroy();
        }
    }

    setCanvasEl = async (canvasWrapperEl: HTMLDivElement, loadSelection: ICrossSectionLoadSelection) => {
        if (this.csCanvas) {
            return;
        }
        try {
            console.log('setCanvasEl:', canvasWrapperEl);
            this.loadSelection = loadSelection;
            if (!this.view) {
                const saved = this.sessionStorageLoadView(loadSelection.alignmentId);
                if (saved) {
                    this.view = saved;
                }
            }
            this.csCanvas = new CrossSectionCanvas({
                zoomToCursorCorrection: this.zoomToCursorCorrection,
                view: this.view,
            });
            if (this.view?.widthMeters) {
                this.initialPositionDone = true;
            }
            await this.csCanvas.init({
                canvasWrapperEl,
                onViewChanged: this.drawAll,
            });
            this.localStorageSave();
            await this.fetchDrawing(loadSelection);
        } catch (e) {
            console.log('e:', e);
            Notify.error('Failed to initialize cross-section scene');
        }
    };

    fetchInitialData = () => {
        this.isLoadingTree = true;
        ProjectCodesService.fetchCodes();

        FileTreeService.fetchFileTree()
            .then(td => {
                const namesMap = {};
                FileTreeService.eachPostOrder(td, 0, node => {
                    if (node.models) {
                        node.models.forEach(m => {
                            namesMap[m.id] = m.name;
                        });
                    }
                });
                this.modelNamesMap = namesMap;
            })
            .catch(Notify.error)
            .finally(() => (this.isLoadingTree = false));
    };

    fetchDrawing = async (loadSelection?: ICrossSectionLoadSelection, resetZoom = false) => {
        this.loadSelection = loadSelection || this.loadSelection;
        this.isLoading = true;
        this.isLoadingAsync = true;
        const shouldFitAfterDataLoad = resetZoom || !this.initialPositionDone;

        let url: string = null;
        let data: ICrossSectionData = null;
        let method: 'GET' | 'POST' = 'GET';

        const commonData = {
            projectId: kuura.getProjectId(),
            alignmentId: this.loadSelection.alignmentId,
            alignmentIndex: this.loadSelection.alignmentIndex,
            w: this.csCanvas.widthMetersInitial > 0 ? this.csCanvas.widthMetersInitial : kuura.getCrossSectionSettings()?.crossSectionWidth ?? 60,
            logpointDelta: this.settings.searchDisplacement,
            folderId: this.loadSelection.folderId,
            includeModelIds: this.loadSelection.includeModelIds?.join(','),
            // Only sent when the caller made an explicit, exhaustive selection
            // (e.g. the volume tool). Omitted otherwise so existing callers keep
            // the server's "no ids => all models" default behaviour.
            strictModelSelection: this.loadSelection.strictModelSelection || undefined,
        };

        if (this.isFreeCrossSection) {
            if (this.csCanvas.widthMetersInitial === 0) {
                const [n1, e1] = latLonToEN(this.freeCrossSectionCoords.lat1, this.freeCrossSectionCoords.lon1);
                const [n2, e2] = latLonToEN(this.freeCrossSectionCoords.lat2, this.freeCrossSectionCoords.lon2);
                const dx = Math.abs(n1 - n2);
                const dy = Math.abs(e1 - e2);
                this.csCanvas.widthMeters = Math.ceil(Math.sqrt(dx * dx + dy * dy));
                this.csCanvas.widthMetersInitial = this.csCanvas.widthMeters;
            }
            url = 'free_cross_section.json';
            data = {
                ...commonData,
                w: this.csCanvas.widthMeters,
                lat1: this.freeCrossSectionCoords.lat1,
                lon1: this.freeCrossSectionCoords.lon1,
                lat2: this.freeCrossSectionCoords.lat2,
                lon2: this.freeCrossSectionCoords.lon2,
            };
        } else {
            url = 'cross_section.json';
            //method = 'POST';
            data = {
                ...commonData,
                station: this.settings.station,
                //includeLogpointIds: this.loadSelection.includeLogpointIds,
            };
        }

        Ajax2({
            url,
            method,
            data,
            timeout: 5 * 60 * 1000,
        })
            .then((resp: { crossSection: ICrossSectionResponse }) => {
                resp.crossSection.models.forEach(model => {
                    model.lines.forEach((line, i) => {
                        line.id = model.modelId + '_' + i;
                    });
                });

                if (this.onFetch) {
                    this.onFetch(resp.crossSection);
                }

                if (resetZoom) {
                    this.csCanvas.panX = 0;
                    this.csCanvas.panY = 0;
                    this.csCanvas.widthMeters = 0;
                    this.initialPositionDone = false;
                }

                this.updateBounds(resp.crossSection);
                this.csCanvas.widthMetersInitial = parseInt(resp.crossSection.width.toFixed(0));
                if (this.csCanvas.widthMeters === 0) {
                    this.csCanvas.widthMeters = this.csCanvas.widthMetersInitial;
                }
                this.settings.station = parseFloat(resp.crossSection.station.toFixed(2));
                this.settings.searchDisplacement = resp.crossSection.logpointDelta;

                this.latestServerData = resp.crossSection;

                this.clearAllTools();
                this.drawAll();

                if (this.realityCaptureIncluded) {
                    const firstCood = this.isFreeCrossSection ? resp.crossSection.coordinates.coords1 : resp.crossSection.coordinates.coords2;
                    const secondCoord = this.isFreeCrossSection ? resp.crossSection.coordinates.coords2 : resp.crossSection.coordinates.coords1;

                    this.getRealityCaptureForCrossSection(firstCood, secondCoord)
                        .then(() => {
                            this.updateBounds(resp.crossSection);
                            if (shouldFitAfterDataLoad) {
                                this.csCanvas.fitToData();
                            }
                            this.drawAll();
                        })
                        .finally(() => {
                            this.isLoadingAsync = false;
                        });
                } else {
                    this.isLoadingAsync = false;
                }
            })
            .catch(e => {
                //showErrors(data);
                Notify.error('Failed to load Cross Section');
                console.log('e:', e);
                this.isLoadingAsync = false;
            })
            .finally(() => {
                if (!this.isFreeCrossSection && this.latestServerData && this.latestServerData.station) {
                    Events.trigger('station-cross-section-loaded', { station: Math.round(this.latestServerData.station) });
                }

                if (this.isFreeCrossSection) {
                    Events.trigger('free-cross-section-loaded', { coords: this.freeCrossSectionCoords });
                }

                this.isLoading = false;
            });
    };

    async getRealityCaptureForCrossSection(start: NorthingEasting, end: NorthingEasting): Promise<void> {
        const { area, startVector, endVector, mercatorDistortion } = await crossSectionCoordsMeta(start, end, 0.1);

        // LumiDB
        const lumi = await lumiDbStore.lumiPointsToCrossData({
            LumiDbUuids: this.loadSelection.includeLumiDbUuids,
            area,
            startVector,
            endVector,
            mercatorDistortion,
            csCanvas: this.csCanvas,
            pointCloudNamesMap: this.realityCaptureNamesMap,
        });

        // DEM
        const includeDemUuids = this.loadSelection.includeDemUuids?.filter(u => !lumi.lumiDbUuids.includes(u));
        const dem = await demStore.getCrossSectionData({
            includeDemUuids,
            area,
            startVector,
            endVector,
            rectangleWidth: 0.1,
            mercatorDistortion,
            csCanvas: this.csCanvas,
            pointCloudNamesMap: this.realityCaptureNamesMap,
        });

        this.selectedRcUuids = [...lumi.lumiDbUuids, ...dem.DemUuids];
        this.realityCapturePoints = [...lumi.lumiDbPoints, ...dem.DemPoints];
        this.realityCaptureModels = [...lumi.lumiDbModels, ...dem.DemModels];
    }

    localStorageSave = () => {
        localStorage.setItem('cross-section-window-settings', JSON.stringify(this.settings));
    };

    localStorageLoad = () => {
        try {
            const settings = JSON.parse(localStorage.getItem('cross-section-window-settings'));
            if (settings) {
                this.settings.step = settings.step ?? 10;
                this.settings.searchDisplacement = settings.searchDisplacement ?? kuura.getCrossSectionSettings().logpointSearchDisplacement ?? 2.5;
                this.settings.deltaZFilter = settings.deltaZFilter ?? 0;
            }
        } catch {
            console.warn('Failed to load cross-section window settings from localStorage');
        }
    };

    getSessionStorageViewKey = (alignmentId?: number) => {
        return `cs-view-state-${kuura.getProjectId()}-${alignmentId ?? ''}`;
    };

    sessionStorageLoadView = (alignmentId: number): CrossSectionViewState => {
        if (alignmentId == null) {
            return null;
        }

        try {
            const viewStateString = window.sessionStorage.getItem(this.getSessionStorageViewKey(alignmentId));
            if (!viewStateString) {
                return null;
            }

            return JSON.parse(viewStateString);
        } catch {
            return null;
        }
    };

    sessionStorageSaveView = _.debounce(() => {
        if (!this.csCanvas) {
            return;
        }

        const viewState = this.csCanvas.getViewState();
        if (!viewState?.widthMeters) {
            return;
        }

        try {
            window.sessionStorage.setItem(this.getSessionStorageViewKey(this.loadSelection.alignmentId), JSON.stringify(viewState));
        } catch {}
    }, 200);

    drawAll = () => {
        //console.time('drawAll');
        const data: ICrossSectionResponse = this.latestServerData;
        if (!data) {
            console.log('drawAll - no data, returning');
            return;
        }

        this.csCanvas.clearStage();

        if (!this.initialPositionDone) {
            this.initialPositionDone = true;
            this.csCanvas.fitToData();
        }

        this.csCanvas.drawGrid();
        //this.csCanvas.drawDebug();

        if (data.alignment) {
            this.drawAlignment(data.alignment, false);
        }

        if (!this.isFreeCrossSection && data.station >= 0) {
            this.csCanvas.drawHeader(data.alignment.props.name, data.stationEquation ? data.stationEquation : data.station.toFixed(2) + ' ' + kuura.getUnit().abbr);
        }

        for (const alignment of data.backgroundAlignments || []) {
            this.drawAlignment(alignment, true);
        }

        for (const border of data.borders) {
            CrossSectionModel.drawBorder(border, this.csCanvas);
        }

        for (const border of data.customBorders) {
            CrossSectionModel.drawBorder(border, this.csCanvas);
        }

        // Pipes
        for (const pipe of data.pipes) {
            pipe.name = this.modelNamesMap[pipe.modelId];
            CrossSectionPipe.drawPipe({
                id: 'pipe_' + pipe.modelId,
                pipe,
                csCanvas: this.csCanvas,
                visible: !this.hiddenPipeIds.includes(pipe.modelId),
                onClickCallback: (pipe, x, y) => {
                    this.currentInfoItem = 'pipe';
                    this.drawMeasurement(pipe, x, y, 11);
                },
            });
        }
        for (const model of data.models) {
            this.drawModel(model, 'model');
        }

        // Reality capture
        if (this.realityCaptureIncluded) {
            if (this.asPointCloud) {
                // Visualize as point cloud
                this.realityCapturePoints.forEach(point => {
                    if (!this.hiddenRcUuids.includes(point.sourceFileUuid)) {
                        CrossSectionPoint.drawPoint(point);
                    }
                });
            } else {
                // Visualize as models
                this.realityCaptureModels.forEach(model => {
                    if (!this.hiddenRcUuids.includes(model.modelUuid)) {
                        this.drawModel(model, 'realityCaptureModel');
                    }
                });
            }
        }

        // Terrain model
        for (const model of data.terrainModels) {
            this.drawModel(model, 'terrainModel');
        }

        this.csCanvas.drawDistanceTool();
        this.csCanvas.drawAreaTool();

        // redraw measurements
        if (this.clickedPoints.length) {
            const center = this.csCanvas.getCenter();
            this.clickedVertex0 = null;
            const canvasOptions = this.csCanvas.canvasOptions;
            const list = [...this.clickedPoints];
            this.clickedPoints = [];
            list.forEach(point => {
                const x = center + point.point.x * this.csCanvas.canvasOptions.x_scale;
                const y =
                    this.csCanvas.height -
                    canvasOptions.bottom_margin +
                    this.csCanvas.lowestPoint * canvasOptions.y_scale -
                    point.point.y * canvasOptions.y_scale +
                    this.csCanvas.panY;
                this.drawMeasurement(point.point, x, y, point.markerSize);
            });
        }

        for (const logpoint of this.getVisibleLogpoints()) {
            const modelId = logpoint.modelId ?? -1;

            if (this.hiddenLogpointsByModelId.includes(modelId)) {
                continue;
            }

            if (this.settings.deltaZFilter != null && logpoint.modelDeltaZ != null) {
                if (Math.abs(logpoint.modelDeltaZ) < this.settings.deltaZFilter) {
                    continue;
                }
            }

            CrossSectionLogpoint.drawLogpoint({
                id: 'model_' + modelId,
                logpoint,
                isHighlighted: logpoint.id === this.loadSelection?.selectedLogpointId,
                csCanvas: this.csCanvas,
                onClickCallback: (logpoint, x, y) => {
                    this.drawMeasurement(logpoint, x, y, 11);
                    this.currentInfoItem = 'logpoint_' + logpoint.id;
                },
            });
        }

        this.sessionStorageSaveView();
    };

    drawAlignment = (alignment: Alignment, isBackground: boolean) => {
        alignment.name = this.modelNamesMap[alignment.id ?? alignment.modelId];
        CrossSectionAlignment.drawAlignment({
            alignment,
            isBackground,
            csCanvas: this.csCanvas,
            onClickCallback: (alignment, x, y) => {
                console.log('alignment:', alignment);
                this.drawMeasurement(alignment, x, y, 11);
            },
        });
    };

    drawModel = (model: ICrossSectionModel, type: 'model' | 'terrainModel' | 'realityCaptureModel') => {
        if (this.hiddenModelIds.includes(model.modelId) || model.empty) {
            return;
        }

        model.name = this.modelNamesMap[model.id ?? model.modelId] ?? model.name;
        CrossSectionModel.drawModel({
            model,
            type,
            csCanvas: this.csCanvas,
            hiddenLineIds: this.hiddenModelIds,
            onClickCallback: (point, x, y) => {
                this.drawMeasurement(point, x, y, 7);
                this.currentInfoItem = 'vertex';
            },
        });
    };

    updateBounds(cs: ICrossSectionResponse) {
        let xMin = Infinity;
        let xMax = -Infinity;
        let yMin = Infinity;
        let yMax = -Infinity;

        const checkPoint = (x: number, y: number) => {
            if (x < xMin) xMin = x;
            if (x > xMax) xMax = x;
            if (y < yMin) yMin = y;
            if (y > yMax) yMax = y;
        };

        const checkModel = (model: ICrossSectionModel) => {
            model.lines?.forEach(line => line.vectors.forEach(v => v.points.forEach(p => checkPoint(p.x, p.y))));
            model.points?.forEach(modelPoint => checkPoint(modelPoint.point.x, modelPoint.point.y));
        };

        [...cs.models, ...cs.terrainModels, ...this.realityCaptureModels].forEach(checkModel);
        this.realityCapturePoints.forEach(point => checkPoint(point.point.x, point.point.y));
        (cs.logpoints ?? []).forEach(logpoint => checkPoint(logpoint.x, logpoint.y));
        [cs.alignment, ...(cs.backgroundAlignments ?? [])].forEach(a => a?.point && checkPoint(a.point.x, a.point.y));
        [...(cs.borders ?? []), ...(cs.customBorders ?? []), ...(cs.pipes ?? [])].forEach(b => b?.point && checkPoint(b.point.x, b.point.y));

        this.csCanvas.lowestPoint = isFinite(yMin) ? Math.floor(yMin) : 0;
        this.csCanvas.highestPoint = isFinite(yMax) ? Math.ceil(yMax) : 30;
        this.csCanvas.dataXMin = isFinite(xMin) ? Math.floor(xMin) : -cs.width / 2;
        this.csCanvas.dataXMax = isFinite(xMax) ? Math.ceil(xMax) : cs.width / 2;
    }

    drawMeasurement = (point: PointInput, x: number, y: number, clickMarkerSize: number) => {
        const actualPoint: ICrossSectionPoint = 'point' in point ? point.point : point;

        const pt: ICrossSectionVertex = {
            point: { ...actualPoint },
            markerSize: clickMarkerSize,
            x,
            y,
        };
        this.csCanvas.clearTooltips();
        this.clickedPoints.push(pt);
        CrossSectionPointsMeasurement.drawMeasurement(pt, x, y, this, this.csCanvas.measureTooltipLayer, this.csCanvas.tooltipLayer);
    };

    stationIncrease = () => {
        this.settings.station = this.settings.station + this.settings.step;
        this.fetchDrawing();
    };

    stationDecrease = () => {
        if (this.settings.station > 0) {
            this.settings.station = Math.max(0, this.settings.station - this.settings.step);
            this.fetchDrawing();
        }
    };

    createPdf = async () => {
        this.isCreatingPdf = true;
        const filename = this.latestServerData.station.toFixed(2) + '.pdf';
        await CrossSectionUtils.createPdf(filename, this.csCanvas.stage, this.getModelsControls);
        this.isCreatingPdf = false;
    };

    clearAllTools = () => {
        this.clickedPoints = [];
        this.clickedVertex0 = null;
        this.clickedVertex1 = null;
        if (this.csCanvas?.stage) {
            this.csCanvas.clearAllTools();
        }
    };

    zoomToModel = (modelId: number | string) => {
        if (!this.latestServerData || !this.csCanvas) return;

        let xMin = Infinity;
        let xMax = -Infinity;
        let yMin = Infinity;
        let yMax = -Infinity;

        const checkPoint = (x: number, y: number) => {
            if (x < xMin) xMin = x;
            if (x > xMax) xMax = x;
            if (y < yMin) yMin = y;
            if (y > yMax) yMax = y;
        };

        const checkPipe = (pipe: Pipe) => {
            const diameter = parseFloat(pipe.props?.diameter);
            const thickness = parseFloat(pipe.props?.thickness);
            const diameterValue = isNaN(diameter) ? 0 : diameter;
            const thicknessValue = isNaN(thickness) ? 0 : thickness;
            const elevOffset = getPipeElevationOffset(diameterValue, thicknessValue, pipe.props?.elevType, pipe.props?.pipeNetType);
            const outerRadius = Math.max(diameterValue / 2 + thicknessValue, 0.25);
            const centerY = pipe.point.y + elevOffset;

            checkPoint(pipe.point.x - outerRadius, centerY - outerRadius);
            checkPoint(pipe.point.x + outerRadius, centerY + outerRadius);
        };

        for (const model of [...this.latestServerData.models, ...this.latestServerData.terrainModels, ...this.realityCaptureModels]) {
            const matchedLine = model.lines?.find(line => line.id == modelId);
            if (matchedLine) {
                matchedLine.vectors.forEach(vector => vector.points.forEach(point => checkPoint(point.x, point.y)));
                continue;
            }

            if (model.modelId != modelId && model.modelUuid != modelId) continue;
            model.lines?.forEach(line => line.vectors.forEach(vector => vector.points.forEach(point => checkPoint(point.x, point.y))));
            model.points?.forEach(modelPoint => checkPoint(modelPoint.point.x, modelPoint.point.y));
        }

        for (const pipe of this.latestServerData.pipes) {
            if (pipe.modelId != modelId) continue;
            if (pipe.point) {
                checkPipe(pipe);
            }
        }

        for (const logpoint of this.getVisibleLogpoints()) {
            if (logpoint.modelId != modelId) continue;
            checkPoint(logpoint.x, logpoint.y);
        }

        for (const point of this.realityCapturePoints) {
            if (point.sourceFileUuid != modelId) continue;
            checkPoint(point.point.x, point.point.y);
        }

        if (!isFinite(xMin) || !isFinite(xMax) || !isFinite(yMin) || !isFinite(yMax)) return;

        const minExtent = 2;
        const xRange = xMax - xMin;
        if (xRange < minExtent) {
            const xCenter = (xMin + xMax) / 2;
            xMin = xCenter - minExtent / 2;
            xMax = xCenter + minExtent / 2;
        }

        const yRange = yMax - yMin;
        if (yRange < minExtent) {
            const yCenter = (yMin + yMax) / 2;
            yMin = yCenter - minExtent / 2;
            yMax = yCenter + minExtent / 2;
        }

        this.csCanvas.zoomToExtent(xMin, xMax, yMin, yMax);
    };

    zoomToCursorCorrection = () => {
        const canvasOptions = this.csCanvas.canvasOptions;
        const lowestPoint = this.csCanvas.lowestPoint;
        const mouseX = this.csCanvas.mouseX;
        const mouseY = this.csCanvas.mouseY;

        const pointX = (this.csCanvas.mouseX - this.csCanvas.getCenter()) / this.csCanvas.oldXScale;
        this.csCanvas.panX = Math.round(mouseX - this.csCanvas.width / 2 - pointX * canvasOptions.x_scale);

        const pointY = (this.csCanvas.height - canvasOptions.bottom_margin + lowestPoint * this.csCanvas.oldYScale + this.csCanvas.panY - mouseY) / this.csCanvas.oldYScale;
        this.csCanvas.panY = mouseY + pointY * canvasOptions.y_scale - lowestPoint * canvasOptions.y_scale - (this.csCanvas.height - canvasOptions.bottom_margin);
    };

    getState = () => {
        if (!this.csCanvas) {
            return null;
        }

        let mapLine: [[number, number], [number, number]] = null;

        if (this.isFreeCrossSection) {
            const { lat1, lon1, lat2, lon2 } = this.freeCrossSectionCoords;
            if (lat1 != null && lon1 != null && lat2 != null && lon2 != null) {
                mapLine = [
                    [lat1, lon1],
                    [lat2, lon2],
                ];
            }
        } else if (this.latestServerData?.coordinates) {
            try {
                const c1 = CoordsUtils.projectCoordinateToWgs84(this.latestServerData.coordinates.coords1);
                const c2 = CoordsUtils.projectCoordinateToWgs84(this.latestServerData.coordinates.coords2);
                mapLine = [
                    [c2.lat, c2.lon],
                    [c1.lat, c1.lon],
                ];
            } catch (e) {
                console.warn('[CS] Failed to convert cross section map line coords', e);
            }
        }

        return {
            loadSelection: this.loadSelection,
            station: this.settings.station > 0 ? this.settings.station : null,
            step: this.settings.step,
            searchDisplacement: this.settings.searchDisplacement,
            deltaZFilter: this.settings.deltaZFilter,
            view: this.csCanvas.getViewState(),
            ...this.freeCrossSectionCoords,
            mapLine,
        };
    };

    getVisibleLogpoints = () => {
        if (this.loadSelection?.includeLogpointIds?.length) {
            return this.latestServerData?.logpoints.filter(logpoint => this.loadSelection.includeLogpointIds.includes(logpoint.id));
        }
        return this.latestServerData?.logpoints ?? [];
    };
}

type PointInput = ICrossSectionPoint | { point: ICrossSectionPoint };
